import { ColorWithTones, ToneVariant } from '@theme/types/colorHarmony.types.ts';
import { checkContrastWCAG, hexToHsl, hslToHex, normalizeHex } from '@theme/utils/colorConversions.ts';

const clamp = (value: number, min: number, max: number): number =>
    Math.min(max, Math.max(min, value));

const buildColor = (h: number, s: number, l: number): string =>
    hslToHex(h, clamp(s, 0, 100), clamp(l, 0, 100));

export const getContrastColor = (backgroundHex: string): string => {
    const background = normalizeHex(backgroundHex);
    const blackRatio = checkContrastWCAG('#000000', background).ratio;
    const whiteRatio = checkContrastWCAG('#FFFFFF', background).ratio;
    return blackRatio >= whiteRatio ? '#000000' : '#FFFFFF';
};

export const generateTones = (baseHex: string): ColorWithTones => {
    const base = normalizeHex(baseHex);
    const { h, s } = hexToHsl(base);

    const c1 = buildColor(h, s * 0.3, 90);
    const c2 = buildColor(h, s * 0.5, 75);
    const o1 = buildColor(h, s * 0.9, 15);
    const o2 = buildColor(h, s * 0.8, 30);

    return {
        base,
        c1,
        c2,
        o1,
        o2,
        contrastOnLight: getContrastColor(c1),
        contrastOnDark: getContrastColor(o1),
    };
};

export const classifyTone = (hex: string): ToneVariant => {
    const { l } = hexToHsl(hex);
    if (l >= 85) {
        return 'c1';
    }
    if (l >= 65) {
        return 'c2';
    }
    if (l <= 20) {
        return 'o1';
    }
    if (l <= 40) {
        return 'o2';
    }
    return 'base';
};

export const validatePaletteContrast = (
    palette: ColorWithTones[],
): { valid: boolean; issues: string[] } => {
    const issues: string[] = [];

    palette.forEach((color, index) => {
        // En lugar de chequear contra un color de texto predefinido para todo el grupo,
        // verificamos si CADA variante tiene al menos UN color de texto estándar (blanco o negro)
        // que cumpla con AA.
        const allVariants = [color.base, color.c1, color.c2, color.o1, color.o2];
        const variantNames = ['Base', 'Claro 1', 'Claro 2', 'Oscuro 1', 'Oscuro 2'];

        allVariants.forEach((variant, vIndex) => {
            const optimalTextColor = getContrastColor(variant);
            const result = checkContrastWCAG(optimalTextColor, variant);

            if (!result.passAA) {
                issues.push(
                    `Color ${index + 1} (${variantNames[vIndex]}) tiene bajo contraste incluso con ${optimalTextColor === '#000000' ? 'negro' : 'blanco'
                    } (${result.ratio.toFixed(2)}:1).`,
                );
            }
        });
    });

    return { valid: issues.length === 0, issues };
};

export const adjustForContrast = (
    colorHex: string,
    backgroundHex: string,
    targetRatio: number,
): string => {
    let adjusted = normalizeHex(colorHex);
    const background = normalizeHex(backgroundHex);
    let { h, s, l } = hexToHsl(adjusted);

    let currentRatio = checkContrastWCAG(adjusted, background).ratio;
    if (currentRatio >= targetRatio) {
        return adjusted;
    }

    const backgroundIsLight = checkContrastWCAG('#000000', background).ratio >
        checkContrastWCAG('#FFFFFF', background).ratio;

    for (let step = 0; step < 100 && currentRatio < targetRatio; step += 1) {
        l = backgroundIsLight ? l - 1 : l + 1;
        l = clamp(l, 0, 100);
        adjusted = buildColor(h, s, l);
        currentRatio = checkContrastWCAG(adjusted, background).ratio;
        if (l === 0 || l === 100) {
            break;
        }
    }

    return adjusted;
};
